/*
 * Applies a widget's `look` and `rules` (docs/hmi-panel-ausbau.md §4).
 *
 * Only widgets that carry a look or rules get this frame; everything else
 * renders exactly as before. With a frame, the frame draws the tile and the
 * grid cell around it steps back (hmiCell.css, `:has(> .hv-look)`), so the
 * author decides between card, plain, outline and accent per widget.
 *
 * Rules run in the HMI script language against the widget's named points.
 * The first rule whose condition holds wins; a rule can hide the widget.
 */

import type { ComponentChildren } from 'preact';
import type { WidgetDefinition, WidgetLook } from '../widgets';
import type { IPointBindingSource } from '../binding';
import { evaluateCondition } from '../script/hmiScript';
import { isHexColor } from '../themeModel';
import { useNamedPoints } from './namedPoints';
import { useHmiRuntime } from './runtimeContext';
import './panel.css';

interface Props {
  widget: WidgetDefinition;
  source: IPointBindingSource | null;
  children: ComponentChildren;
}

export function needsLookFrame(widget: WidgetDefinition): boolean {
  return Boolean(widget.look) || Boolean(widget.rules && widget.rules.length > 0);
}

export function LookFrame({ widget, source, children }: Props) {
  const { ctx } = useNamedPoints(widget.points, source);
  const { editing } = useHmiRuntime();

  let look: WidgetLook = { ...(widget.look ?? {}) };
  let hidden = false;
  for (const rule of widget.rules ?? []) {
    if (evaluateCondition(rule.when, ctx)) {
      look = { ...look, ...(rule.look ?? {}) };
      hidden = rule.hidden === true;
      break;
    }
  }
  // In the editor a hidden widget stays visible, faded, so it can be found.
  if (hidden && !editing) return null;

  const variant = look.variant ?? 'card';
  const style: Record<string, string> = {};
  if (isHexColor(look.textColor)) style['--look-text'] = look.textColor;
  if (isHexColor(look.background)) style['--look-bg'] = look.background;
  if (isHexColor(look.borderColor)) style['--look-border'] = look.borderColor;
  if (typeof look.radius === 'number') style['--look-radius'] = `${clamp(look.radius, 0, 400)}px`;
  if (typeof look.padding === 'number') style['--look-padding'] = `${clamp(look.padding, 0, 200)}px`;
  if (typeof look.fontScale === 'number') style['--look-scale'] = String(clamp(look.fontScale, 0.5, 6));

  const cls = [
    'hv-look',
    `hv-look--${variant}`,
    look.align ? `hv-look--align-${look.align}` : '',
    hidden ? 'hv-look--hidden' : '',
  ].filter(Boolean).join(' ');

  return <div class={cls} style={style}>{children}</div>;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(Math.max(v, lo), hi);
}
